import { useEffect, useState, useContext } from 'react'
import {
  collection,
  onSnapshot,
  query,
  where,
  deleteDoc,
  doc,
  DocumentChange,
} from 'firebase/firestore'
import { firestoreDb } from '@/firebaseConfig'
import { UserProfileContext } from '@view/providers/UserProfileProvider'
import { UserMessage } from '@models/UserMessage'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { Box } from '@mui/material'
import log from 'loglevel'

const UserMessageAdapter = () => {
  const { user } = useContext(UserProfileContext)
  const [messages, setMessages] = useState<Map<string, UserMessage>>(new Map())

  useEffect(() => {
    const aclIdentities = user?.profile?.aclIdentities
    if (!aclIdentities?.length) return

    const fbQuery = query(
      collection(firestoreDb, 'userMessages'),
      where('acl.entity_list', 'array-contains-any', aclIdentities),
    )

    const unsubscribe = onSnapshot(
      fbQuery,
      (snapshot) => {
        snapshot.docChanges().forEach((change: DocumentChange) => {
          const data = change.doc.data()
          const msg = new UserMessage({
            ...(data as UserMessage),
            id: change.doc.id,
          })

          setMessages((prev) => {
            const updated = new Map(prev)
            change.type !== 'removed'
              ? updated.set(msg.id, msg)
              : updated.delete(msg.id)
            return updated
          })
        })
      },
      (error) => {
        log.error('Firestore fetch error:', error)
      },
    )

    return () => unsubscribe()
  }, [user?.profile?.aclIdentities])

  const handleDismiss = async (id: string) => {
    await deleteDoc(doc(firestoreDb, 'userMessages', id))
  }

  return (
    <Box p={1}>
      {Array.from(messages.values()).map((msg) => (
        <Alert
          key={msg.id}
          severity={msg.severity.toLowerCase() as 'info' | 'warning' | 'error'}
          action={
            'userMessages_manager' in msg.acl?.role_identities && (
              <IconButton size="small" onClick={() => handleDismiss(msg.id)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            )
          }
          sx={{ mb: 1 }}
        >
          {msg.message}
        </Alert>
      ))}
    </Box>
  )
}

export default UserMessageAdapter
